'use client';

import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useAnalytics } from '../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../helpers/analytics/trackEvent';
import { IBoundingBox } from '../../interfaces/geo';
import {
  GetPlaceTopSpecies_entry_topSpeciesSummary_edges_node,
  GetBasicSpeciesInfoFromList_species_edges_node,
} from '../../interfaces/graphql';
import { PLACE_SUB_LINK_IDS } from '../../views/Place/helpers/getPlaceSubNavLinks';
import { ITopSpeciesFishingSpots } from '../../views/Place/Place';
import { AppDownloadModal } from '../AppDownloadModal/AppDownloadModal';
import { Heading } from '../Heading/Heading';
import { TopSpeciesCard } from '../TopSpeciesCard/TopSpeciesCard';
import { TopSpeciesCTA } from '../TopSpeciesCTA/TopSpeciesCTA';
import { Button } from '../Clickables/Buttons/Button';

const DEFAULT_MAX_VISIBLE_ITEMS = 3;

interface IProps {
  appDownloadModalHeader?: string;
  boundingBox?: IBoundingBox;
  className?: string;
  clickEventSourceId?: string;
  headingText?: string;
  topSpecies:
    | GetPlaceTopSpecies_entry_topSpeciesSummary_edges_node[]
    | GetBasicSpeciesInfoFromList_species_edges_node[];
  topSpeciesFishingSpots: ITopSpeciesFishingSpots[];
  nrOfVisibleItems?: number;
}

export const TopSpeciesSection = ({
  appDownloadModalHeader = `Download the Fishbrain app to see all species from this area`,
  boundingBox,
  clickEventSourceId,
  headingText = `Top fish species in this area`,
  topSpecies,
  className,
  topSpeciesFishingSpots,
  nrOfVisibleItems = DEFAULT_MAX_VISIBLE_ITEMS,
}: IProps) => {
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const [visibleTopSpeciesItems, setVisibleTopSpeciesItems] = useState(nrOfVisibleItems);
  const [showDownloadCTA, setShowDownloadCTA] = useState(false);
  const { currentPage } = useAnalytics();

  const speciesCount = topSpecies.length;

  const handleShowMore = () => {
    // First click -> show all species
    if (visibleTopSpeciesItems < speciesCount) {
      topSpecies.map((_, index) => {
        if (index >= visibleTopSpeciesItems) {
          setVisibleTopSpeciesItems(prev => prev + 1);
        }
        return null;
      });
      // Second click -> show download CTA
    } else {
      setShowDownloadCTA(true);
      return;
    }
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'TopSpeciesSection',
        source_id: clickEventSourceId,
        type: 'read-more',
        page: currentPage,
      },
    });
  };

  const handleCTAClick = () => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'TopSpeciesSection',
        source_id: clickEventSourceId,
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  return (
    <div
      id={PLACE_SUB_LINK_IDS.TOP_SPECIES}
      className={twMerge(
        'sm:bg-lake-fog scroll-mt-(--navigation-scrolltop-offset-mobile) sm:scroll-mt-(--navigation-scrolltop-offset) sm:rounded-2xl',
        className,
      )}
    >
      <Heading className="mb-4 sm:mb-8" variant="sm" as="h2">
        {headingText}
      </Heading>
      <div className="flex flex-col">
        {topSpecies.map(({ image, _id, displayName }, index) => {
          const imageUrl = image.url || '';
          const imageWidth = image.width || 0;
          const imageHeight = image.height || 0;

          return (
            <TopSpeciesCard
              boundingBox={index >= visibleTopSpeciesItems ? undefined : boundingBox}
              key={`species-${_id}`}
              speciesId={_id}
              name={displayName}
              className={`w-full ${index >= visibleTopSpeciesItems ? 'hidden' : ''}`}
              imageUrl={imageUrl}
              imageWidth={imageWidth}
              imageHeight={imageHeight}
              clickEventPosition={index}
              clickEventSourceId={clickEventSourceId}
              clickEventSource="TopSpeciesSection"
              nrOfFishingSpots={topSpeciesFishingSpots[index]?.nrOfWaters}
            />
          );
        })}
        {showDownloadCTA ? (
          <TopSpeciesCTA onCTAClick={handleCTAClick} sourceId={clickEventSourceId} />
        ) : (
          <Button size="sm" className="mt-2 p-2" variant="textPrimary" onClick={handleShowMore}>
            Show more species
          </Button>
        )}
      </div>
      <AppDownloadModal
        appDownloadSource="TopSpeciesSection"
        appDownloadSourceId={clickEventSourceId}
        modalHeader={appDownloadModalHeader}
        isModalOpen={isAppDownloadModalOpen}
        onCloseModal={() => setIsAppDownloadModalOpen(false)}
      />
    </div>
  );
};
